import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { AlertCircle, Bot, CheckCircle2, Database, FolderCheck, Handshake, Inbox as InboxIcon, Loader2, Mail, PenSquare, RefreshCw, Send, Settings2, Sparkles, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion } from "@/contexts/RegionContext";
import { deptInboxApi } from "@/lib/api";
import { setCollectionsRegion } from "@/lib/regionFmt";
import { EmailList, InboxSearch, InboxTabs } from "@/pages/inbox/EmailList";
import { ForwardDialog } from "@/pages/inbox/ForwardDialog";
import { DeptReplyDialog, LogToCaseDialog } from "@/pages/inbox/DeptDialogs";
import { DeptComposeDialog } from "@/pages/inbox/ComposeDialog";
import { MoveDoneDialog } from "@/pages/inbox/InboxActions";
import { DeptReadingPane } from "@/pages/inbox/DeptReadingPane";
import { CATEGORY_TONE } from "@/pages/inbox/CasePicker";
import { FlagChips } from "@/pages/inbox/TriageChips";
import { useHiddenEmails, usePullCadence } from "@/pages/inbox/useInboxPolling";
import { AutopilotToggle } from "@/pages/inbox/AutopilotToggle";
import { AutopilotLog } from "@/pages/inbox/AutopilotLog";
import { AssigneeFilter, applyAssigneeFilter } from "@/pages/inbox/AssigneeFilter";
import { BulkBar } from "@/pages/inbox/BulkBar";
import { InboxIntelDialog } from "@/pages/inbox/InboxIntelDialog";
import { useIsPhone } from "@/hooks/useIsPhone";
import { PhoneInboxView } from "@/pages/inbox/phone/PhoneInboxView";
import { useSearchParams } from "react-router-dom";

const AUTOPILOT_TAB = "__autopilot";

// One shell for every department mailbox — Accounts, Legal, My mailbox
// (Applications shares the list / tabs / forward / pull pieces but keeps its own actions).
export function InboxShell({ dept, title, icon: Icon, caseLink, testid, hideAutopilot = false, subtitle, embedded = false, region: regionOverride }) {
  const { user } = useAuth();
  const { currentRegion } = useRegion();
  const region = regionOverride || currentRegion;   // /aus/… pages force AUS whatever the switcher says
  const isAus = region === "AUS";
  setCollectionsRegion(region);   // the quick-action wizards (payment plan / statement / payout) follow the inbox's region
  const api = useMemo(() => deptInboxApi(dept, region), [dept, region]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [folder, setFolder] = useState("inbox");
  const [folders, setFolders] = useState([]);
  const [settings, setSettings] = useState(null);
  const [triaging, setTriaging] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [who, setWho] = useState("all");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState(null);       // {q, emails} when a whole-mailbox search is showing
  const [searching, setSearching] = useState(false);
  const [checked, setChecked] = useState(() => new Set());
  const [replyFor, setReplyFor] = useState(null);   // {email, body, replyAll}
  const [forwardFor, setForwardFor] = useState(null);
  const [logFor, setLogFor] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [intelOpen, setIntelOpen] = useState(false);
  const [doneAsk, setDoneAsk] = useState(null);       // email waiting on "move anyway / pick a file"
  const [logThenDone, setLogThenDone] = useState(null); // email to log via the picker, then move to Done
  const { stripHidden, hideEmail } = useHiddenEmails(setData);
  const isLogTab = folder === AUTOPILOT_TAB;
  const isPhone = useIsPhone();
  const [searchParams, setSearchParams] = useSearchParams();
  const openParam = searchParams.get("open");

  const loadFolders = useCallback((force = false) => api.folders(force).then(r => setFolders(r.folders || [])).catch(() => {}), [api]);

  const fetchEmails = useCallback(async (silent = false, f = folder) => {
    if (f === AUTOPILOT_TAB) return;
    try {
      if (!silent) setLoading(true);
      const r = await api.list({ limit: f === "inbox" ? 200 : 100, folder: f });
      setData(stripHidden(r));
      setErr(null);
      setLastSync(new Date());
    } catch (e) {
      if (!silent) setErr(e.response?.data?.detail || e.message);
    } finally { if (!silent) setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, folder]);

  useEffect(() => {
    setData(null); setSelectedId(null); setErr(null); setFolder("inbox"); setFolders([]); setWho("all"); setSearch(null); setQuery(""); setChecked(new Set());
    api.getSettings().then(setSettings).catch(() => {});
    loadFolders();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);
  useEffect(() => { fetchEmails(); }, [fetchEmails]);
  const countdown = usePullCadence(() => { fetchEmails(true); if (folder !== "inbox") loadFolders(true); });

  // Deep link from a push notification / another screen: ?open=<message id> selects that email once the list is in.
  useEffect(() => {
    if (!openParam || !data) return;
    const clear = () => setSearchParams((p) => { const n = new URLSearchParams(p); n.delete("open"); return n; }, { replace: true });
    const hit = (data.emails || []).find(e => e.id === openParam || e.internet_message_id === openParam);
    if (hit) { setSelectedId(hit.id); clear(); return; }
    api.getEmail(openParam).then((r) => {
      const m = r.message || {};
      const from = (m.from || {}).emailAddress || {};
      const row = { id: m.id || openParam, subject: m.subject || "", from_name: from.name || "", from_email: from.address || "", received: m.receivedDateTime || "",
        preview: m.bodyPreview || "", has_attachments: !!m.hasAttachments, is_read: m.isRead !== false, to: (m.toRecipients || []).map(x => x.emailAddress), cc: (m.ccRecipients || []).map(x => x.emailAddress) };
      setData(prev => prev ? { ...prev, emails: [row, ...(prev.emails || []).filter(e => e.id !== row.id)] } : prev);
      setSelectedId(row.id);
    }).catch(() => toast.info("That email isn't in this mailbox any more")).finally(clear);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openParam, data]);

  const allEmails = useMemo(() => {
    const rows = (search ? search.emails : data?.emails) || [];
    if (search || folder !== "sent") return rows;
    // Sent: show who the mail went TO rather than the mailbox itself (Emma 4939f20839)
    return rows.map(e => {
      const first = (e.to || [])[0];
      const label = first ? (first.name || first.address) : "(no recipient)";
      return { ...e, from_name: `To: ${label}${(e.to || []).length > 1 ? ` +${e.to.length - 1}` : ""}`, from_email: first?.address || e.from_email };
    });
  }, [data, search, folder]);
  const emails = useMemo(() => applyAssigneeFilter(allEmails, who, user, unreadOnly), [allEmails, who, user, unreadOnly]);
  const inboxTotal = folder === "inbox" && data?.total != null ? data.total : (data?.emails || []).length;
  const selected = useMemo(() => allEmails.find(e => e.id === selectedId) || null, [allEmails, selectedId]);
  const patchEmail = (id, patch) => {
    const upd = (list) => list.map(e => e.id === id ? { ...e, ...patch } : e);
    setData(prev => prev ? { ...prev, emails: upd(prev.emails) } : prev);
    setSearch(prev => prev ? { ...prev, emails: upd(prev.emails) } : prev);
  };
  const dropEmail = (id) => { hideEmail(id); setSearch(prev => prev ? { ...prev, emails: prev.emails.filter(e => e.id !== id) } : prev); setSelectedId(null); };

  const runSearch = async () => {
    const q = query.trim();
    if (q.length < 2) { toast.info("Type at least 2 characters to search"); return; }
    setSearching(true);
    try {
      const r = await api.search(q);
      setSearch({ q, emails: r.emails || [] }); setSelectedId(null); setChecked(new Set());
      if (!(r.emails || []).length) toast.info(`Nothing found for “${q}” in ${r.mailbox || "the mailbox"}`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); } finally { setSearching(false); }
  };
  const clearSearch = () => { setQuery(""); setSearch(null); setSelectedId(null); setChecked(new Set()); };

  const setAutoTriage = async (v) => {
    const prev = settings;
    setSettings(s => ({ ...s, auto_triage: v }));
    try {
      setSettings(await api.setSettings({ auto_triage: v }));
      toast.success(v ? "Auto-triage on — new emails get a category badge within ~2 min" : "Auto-triage off");
    } catch (e) { setSettings(prev); toast.error(e.response?.data?.detail || e.message); }
  };

  const triageAll = async () => {
    const pending = allEmails.filter(e => !e.triage).slice(0, 20);
    if (!pending.length) { toast.info("Everything visible is already triaged"); return; }
    setTriaging(true);
    let done = 0;
    const pool = [...pending];
    const worker = async () => {
      while (pool.length) {
        const e = pool.shift();
        try { const r = await api.classify(e.id); patchEmail(e.id, { triage: r.triage }); done++; } catch { /* keep going */ }
      }
    };
    await Promise.all([worker(), worker(), worker(), worker()]);
    setTriaging(false);
    toast.success(`Triaged ${done} email${done === 1 ? "" : "s"}`);
  };

  const moveDone = async (email, payload = null) => {
    try {
      const r = await api.moveDone(email.id, payload);
      const where = r.logged?.sf || r.logged?.customer_name;
      toast.success(where ? `Logged on ${where} and moved to Done` : "Email moved to the Done folder");
      dropEmail(email.id);
      await fetchEmails(); loadFolders(true);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "not_logged") { setDoneAsk(email); return; }
      toast.error((typeof d === "string" ? d : d?.message) || e.message);
    }
  };

  const moveTo = async (email, f) => {
    await api.move(email.id, f.kind === "inbox" ? "inbox" : f.kind === "done" ? "done" : f.id);
    dropEmail(email.id);
    await fetchEmails(); loadFolders(true);
  };

  const afterHandled = async (movedDone, id) => {
    if (movedDone) dropEmail(id);
    await fetchEmails(); loadFolders(true);
  };

  const toggleChecked = (id) => setChecked(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const afterBulk = async (r) => {
    if (r.action !== "assign" && r.action !== "mark_read") [...checked].forEach(id => dropEmail(id));
    setChecked(new Set());
    await fetchEmails(); loadFolders(true);
    if (search) runSearch();
  };

  const subFolders = folders.filter(f => f.kind === "folder");
  const doneRow = folders.find(f => f.kind === "done");
  const activeSub = subFolders.find(f => f.id === folder) || null;
  // Dean, 9 Oct 2026 — only Inbox · Done · Autopilot log are tabs; every other mailbox folder lives under "Folders ▾"
  const sentRow = folders.find(f => f.kind === "sent");
  const tabs = [
    { id: "inbox", label: `Inbox (${inboxTotal})`, icon: InboxIcon },
    { id: "done", label: doneRow?.name || "Done", icon: CheckCircle2, count: doneRow?.total },
    { id: "sent", label: "Sent", icon: Send, count: sentRow?.total ?? undefined, title: "Everything sent from this mailbox — including accounts with no timeline" },
    ...(settings?.autopilot_available && !hideAutopilot ? [{ id: AUTOPILOT_TAB, label: "Autopilot log", icon: Bot }] : []),
  ];
  const renderBadges = (e) => (
    <>
      {e.triage && (
        <Badge variant="outline" className={cn("text-[9px]", CATEGORY_TONE[e.triage.category] || CATEGORY_TONE.Other)} data-testid={`category-badge-${e.internet_message_id || e.id}`}>
          {e.triage.category} · {e.triage.confidence}%
        </Badge>
      )}
      <FlagChips flags={e.triage?.flags} idKey={e.internet_message_id || e.id} max={2} />
      {e.triage?.matched_case?.kind === "loandisk" ? (
        <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-800 border-amber-300 gap-0.5" title={`${e.triage.matched_case.customer_name} — in LoanDisk, no portal file yet`} data-testid={`loandisk-badge-${e.internet_message_id || e.id}`}>
          <Database className="w-2.5 h-2.5" />{e.triage.matched_case.sf || e.triage.matched_case.customer_name} · LoanDisk
        </Badge>
      ) : e.triage?.matched_case && (
        <Badge variant="outline" className="text-[9px] bg-indigo-50 text-indigo-700 border-indigo-300" title={e.triage.matched_case.customer_name} data-testid={`matched-case-badge-${e.internet_message_id || e.id}`}>
          {e.triage.matched_case.sf || e.triage.matched_case.customer_name}
        </Badge>
      )}
      {e.triage?.matched_case?.in_arrears && (
        <Badge className="text-[9px] bg-red-600 hover:bg-red-600 text-white gap-0.5" title={`${e.triage.matched_case.customer_name} — ${e.triage.matched_case.days_past_due} days past due`} data-testid={`arrears-badge-${e.internet_message_id || e.id}`}>
          <AlertCircle className="w-2.5 h-2.5" />Arrears · {e.triage.matched_case.days_past_due} dpd
        </Badge>
      )}
      {e.assigned && (
        <Badge variant="outline" className="text-[9px] bg-sky-50 text-sky-700 border-sky-300 gap-0.5" title={e.assigned.name} data-testid={`assigned-badge-${e.internet_message_id || e.id}`}>
          <UserCheck className="w-2.5 h-2.5" />{(e.assigned.name || "").split(" ")[0]}
        </Badge>
      )}
      {(e.filed || []).map((f, i) => (
        <Badge key={i} variant="outline" className={cn("text-[9px] gap-0.5", f.kind === "p2p" ? "bg-amber-50 text-amber-800 border-amber-300" : "bg-green-50 text-green-700 border-green-300")}
          title={f.kind === "p2p" ? "Promise to pay added to the file" : f.kind === "reply" ? "Reply logged on the file" : "Logged on the file"}>
          {f.kind === "p2p" ? <Handshake className="w-2.5 h-2.5" /> : <FolderCheck className="w-2.5 h-2.5" />}{f.kind === "p2p" ? "P2P · " : ""}{f.sf || f.customer_name}
        </Badge>
      ))}
      {e.triage?.prereply && (
        <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-700 border-violet-300 gap-0.5" title="AI reply drafted — open the email to review it" data-testid={`prereply-badge-${e.internet_message_id || e.id}`}>
          <Sparkles className="w-2.5 h-2.5" />draft
        </Badge>
      )}
      {e.triage?.autopilot?.actions?.length > 0 && (
        <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-300 gap-0.5" title={`Autopilot: ${e.triage.autopilot.actions.join(", ")}`} data-testid={`autopilot-badge-${e.internet_message_id || e.id}`}>
          <Bot className="w-2.5 h-2.5" />auto
        </Badge>
      )}
    </>
  );

  const readingPaneNode = selected && !isLogTab ? (
    <DeptReadingPane
      email={selected} api={api} me={user} title={title} caseLink={caseLink}
      folders={folders.filter(f => f.kind !== "sent")} currentFolder={search ? selected.folder_id : folder} onMove={(f) => moveTo(selected, f)}
      onTriaged={(t) => patchEmail(selected.id, { triage: t })}
      onFiled={(f) => f && patchEmail(selected.id, { filed: [...(selected.filed || []), ...(Array.isArray(f) ? f : [f])] })}
      onReply={(draft, source) => setReplyFor({ email: selected, body: typeof draft === "string" ? draft : "", replyAll: false, source: typeof draft === "string" && draft ? (source || "") : "" })}
      onLogToCase={() => setLogFor(selected)}
      onForward={() => setForwardFor(selected)}
      onMoveDone={() => moveDone(selected)}
      onAssigned={(a) => patchEmail(selected.id, { assigned: a })}
    />
  ) : null;

  return (
    <div className={cn(isPhone ? "" : "flex flex-col gap-4 min-h-0 overflow-hidden", !isPhone && (embedded ? "flex-1 pt-3" : "h-[calc(100vh-7rem)]"))} data-testid={testid}>
      {!isPhone && (<>
      <div className="flex flex-wrap items-center justify-between gap-3 shrink-0">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-blue-600" />
          <h1 className="text-xl font-bold" data-testid="inbox-title">{title}</h1>
          <Badge className={cn("text-[10px] font-bold", isAus ? "bg-emerald-600 hover:bg-emerald-600" : "bg-blue-600 hover:bg-blue-600")} data-testid="inbox-region-badge">
            {isAus ? "AUS" : "UK"}
          </Badge>
          <Badge variant="outline" className="font-mono text-[10px]" data-testid="inbox-mailbox-badge">{data?.mailbox || settings?.mailbox || (dept === "mine" ? "…" : `${dept}@…`)}</Badge>
          <Badge variant="outline" className="text-[10px]">{(data?.emails || []).length} emails</Badge>
          {subtitle && <span className="text-[11px] text-muted-foreground" data-testid="inbox-subtitle">{subtitle}</span>}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" className="h-8 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setComposeOpen(true)} data-testid="compose-new-email-btn"
            title={`Write a new email from ${data?.mailbox || settings?.mailbox || "this mailbox"}`}>
            <PenSquare className="w-3.5 h-3.5 mr-1" />New email
          </Button>
          <label className="flex items-center gap-1.5 text-[11px] text-slate-600 border rounded-md px-2 py-1 bg-white cursor-pointer" data-testid="auto-triage-toggle">
            <Sparkles className="w-3 h-3 text-violet-600" />Auto-triage
            <Switch checked={settings?.auto_triage !== false} onCheckedChange={setAutoTriage} className="scale-75" data-testid="auto-triage-switch"
              disabled={settings?.mode === "autopilot"} />
          </label>
          {!hideAutopilot && <AutopilotToggle settings={settings} api={api} onChange={setSettings} />}
          {dept !== "mine" && (
            <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => setIntelOpen(true)} disabled={!settings} data-testid="inbox-intel-btn" title="Autopilot schedule, confidence, rule switches, AI guidance, policy documents and the triage category list">
              <Settings2 className="w-3.5 h-3.5" /><span className="text-[11px]">Filters &amp; intel</span>
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={triageAll} disabled={triaging || loading || isLogTab} data-testid="triage-all-btn">
            {triaging ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1 text-violet-600" />}AI triage
          </Button>
          <Button size="sm" variant="outline" onClick={() => { fetchEmails(); loadFolders(true); }} disabled={loading} data-testid="inbox-refresh-btn"
            title={`Pull the latest mail. Auto-pulls every 30s${lastSync ? ` · last pull ${lastSync.toLocaleTimeString("en-GB")}` : ""}`}>
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            <span className="ml-1 text-[11px]">PullMail</span>
          </Button>
          <span className="text-[10px] text-muted-foreground font-mono tabular-nums" data-testid="next-pull-countdown">next pull {countdown}s</span>
        </div>
      </div>

      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3 shrink-0" data-testid="inbox-error">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 flex-1 min-h-0">
        <Card className="lg:col-span-2 flex flex-col min-h-0 overflow-hidden">
          <InboxTabs tabs={tabs} active={search ? "__search" : activeSub ? "__folders" : folder} onChange={(id) => { setFolder(id); setSelectedId(null); setSearch(null); setQuery(""); setChecked(new Set()); }}
            folders={subFolders} activeFolder={search ? null : activeSub} />
          {isLogTab ? (
            <AutopilotLog api={api} settings={settings} caseLink={caseLink} onSettings={setSettings} />
          ) : (
            <>
              <InboxSearch value={query} onChange={setQuery} onSearch={runSearch} onClear={clearSearch} searching={searching} active={!!search} resultCount={search?.emails.length || 0} />
              <AssigneeFilter emails={allEmails} me={user} value={who} onChange={setWho} unreadOnly={unreadOnly} onUnreadToggle={setUnreadOnly} />
              {checked.size > 0 && (
                <BulkBar count={checked.size} api={api} me={user} ids={[...checked]} folders={folders.filter(f => f.kind !== "sent")} currentFolder={search ? "__search" : folder} onDone={afterBulk} onClear={() => setChecked(new Set())} />
              )}
              <EmailList emails={emails} loading={loading && !search} hasData={!!data || !!search} selectedId={selectedId} onSelect={setSelectedId}
                loadingText={`Loading ${title.toLowerCase()}…`}
                emptyText={search ? `Nothing found for “${search.q}”.` : (who !== "all" || unreadOnly) && allEmails.length ? "No emails match this filter." : folder === "done" ? "The Done folder is empty." : folder === "sent" ? "Nothing has been sent from this mailbox yet." : folder === "inbox" ? "Inbox is empty." : "This folder is empty."}
                renderBadges={renderBadges} selectable checked={checked} onToggle={toggleChecked} />
            </>
          )}
        </Card>
        <Card className="lg:col-span-3 flex flex-col min-h-0 overflow-hidden">
          {selected && !isLogTab ? (
            <div className="flex-1 min-h-0 overflow-y-auto" data-testid="reading-pane-scroll">
              {readingPaneNode}
            </div>
          ) : (
            <CardContent className="p-10 text-center text-sm text-muted-foreground">
              {isLogTab ? <Bot className="w-8 h-8 mx-auto mb-2 text-slate-300" /> : <Mail className="w-8 h-8 mx-auto mb-2 text-slate-300" />}
              {isLogTab ? "Everything Autopilot did is listed on the left" : "Select an email to read it"}
            </CardContent>
          )}
        </Card>
      </div>
      </>)}

      {isPhone && (
        <PhoneInboxView ctx={{
          title, api, user, data, loading, err, folder, tabs, subFolders, folders, allEmails, emails, who, setWho, unreadOnly, setUnreadOnly,
          query, setQuery, runSearch, clearSearch, search, searching, checked, setChecked, toggleChecked, afterBulk, selected, setSelectedId, renderBadges,
          readingPane: readingPaneNode, moveDone, patchEmail, dropEmail, settings, setSettings, setAutoTriage, triageAll, triaging, hideAutopilot, caseLink,
          setComposeOpen, setIntelOpen, isLogTab,
          openFolder: (id) => { setFolder(id); setSelectedId(null); setSearch(null); setQuery(""); setChecked(new Set()); },
          refresh: () => { fetchEmails(); loadFolders(true); },
          openReply: (email, body = "", replyAll = false) => setReplyFor({ email, body: typeof body === "string" ? body : "", replyAll }),
          openForward: (email) => setForwardFor(email),
        }} />
      )}

      {replyFor && (
        <DeptReplyDialog email={replyFor.email} api={api} me={user} caseLink={caseLink}
          fromLabel={data?.mailbox || settings?.mailbox || ""} initialBody={replyFor.body} initialReplyAll={!!replyFor.replyAll} draftSource={replyFor.source || ""}
          suggestedCase={replyFor.email.triage?.matched_case?.case_id ? replyFor.email.triage.matched_case : replyFor.email.filed?.[0] || null}
          onClose={() => setReplyFor(null)}
          onSent={async (r) => { const id = replyFor.email.id; setReplyFor(null); await afterHandled(r.moved_to_done, id); }} />
      )}
      {forwardFor && (
        <ForwardDialog email={forwardFor} forward={api.forward} domain={api.domain}
          onClose={() => setForwardFor(null)}
          onForwarded={async (moved) => { setForwardFor(null); await afterHandled(moved, forwardFor.id); }} />
      )}
      {logFor && (
        <LogToCaseDialog email={logFor} api={api} me={user} caseLink={caseLink}
          suggestedCase={logFor.triage?.matched_case?.case_id ? logFor.triage.matched_case : null}
          onClose={() => setLogFor(null)}
          onLogged={(filed) => { patchEmail(logFor.id, { filed: [...(logFor.filed || []), filed] }); setLogFor(null); }} />
      )}
      {doneAsk && (
        <MoveDoneDialog email={doneAsk}
          onClose={() => setDoneAsk(null)}
          onMoveAnyway={async () => { const e = doneAsk; setDoneAsk(null); await moveDone(e, { force: true }); }}
          onPickFile={() => { setLogThenDone(doneAsk); setDoneAsk(null); }} />
      )}
      {logThenDone && (
        <LogToCaseDialog email={logThenDone} api={api} me={user} caseLink={caseLink}
          suggestedCase={logThenDone.triage?.matched_case?.case_id ? logThenDone.triage.matched_case : null}
          onClose={() => setLogThenDone(null)}
          onLogged={async (filed) => {
            const e = logThenDone; setLogThenDone(null);
            patchEmail(e.id, { filed: [...(e.filed || []), filed] });
            await moveDone(e, { log_case_id: filed?.case_id || "" });
          }} />
      )}
      {composeOpen && (
        <DeptComposeDialog api={api} me={user} caseLink={caseLink}
          fromLabel={data?.mailbox || settings?.mailbox || ""}
          onClose={() => setComposeOpen(false)}
          onSent={() => { setComposeOpen(false); loadFolders(true); }} />
      )}
      {intelOpen && settings && (
        <InboxIntelDialog api={api} settings={settings} isAus={isAus} onChange={setSettings} onClose={() => setIntelOpen(false)} />
      )}
    </div>
  );
}
